// TradersDiary.js
import React, { useState, useMemo, useEffect } from 'react';
import './TradersDiary.css';

const TradersDiary = ({ onBack }) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [selectedMonth, setSelectedMonth] = useState(0); // Jan (0-indexed)
  const [selectedYear, setSelectedYear] = useState(2026);
  const [tradeData, setTradeData] = useState(() => {
    // Load from localStorage on initial mount
    const saved = localStorage.getItem('tradersDiaryData');
    return saved ? JSON.parse(saved) : {};
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [formData, setFormData] = useState({ net: '', label: '', type: '' });

  // Save to localStorage whenever tradeData changes
  useEffect(() => {
    localStorage.setItem('tradersDiaryData', JSON.stringify(tradeData));
  }, [tradeData]);

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  // Convert Sunday=0 to Monday-start week (Mo=0, Tu=1, We=2, Th=3, Fr=4, Sa=5, Su=6)
  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Sunday becomes 6, Monday becomes 0
  };

  const handleDayClick = (day) => {
    const dateKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const existingData = tradeData[dateKey];

    setSelectedDay(day);
    setFormData(existingData ? {
      net: Math.abs(existingData.net),
      label: existingData.label,
      type: existingData.net > 0 ? 'profit' : 'loss'
    } : { net: '', label: '', type: '' });
    setShowModal(true);
  };

  const handleSaveTrade = () => {
    if (!formData.net || !formData.type) return;

    const dateKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    const netValue = formData.type === 'profit' ? Number(formData.net) : -Number(formData.net);

    setTradeData(prev => ({
      ...prev,
      [dateKey]: {
        net: netValue,
        label: formData.label || '',
        trades: 1
      }
    }));
    setShowModal(false);
  };

  const handleDeleteTrade = () => {
    const dateKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    const newData = { ...tradeData };
    delete newData[dateKey];
    setTradeData(newData);
    setShowModal(false);
  };

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth);
    const days = [];

    // Empty slots for days before the 1st of the month
    for (let i = 0; i < firstDay; i++) {
      days.push({ type: 'empty' });
    }

    // Actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const data = tradeData[dateKey];
      days.push({
        type: 'day',
        day,
        data,
        hasProfit: data && data.net > 0,
        hasLoss: data && data.net < 0,
      });
    }

    return days;
  }, [selectedMonth, selectedYear, tradeData]);

  const stats = useMemo(() => {
    const monthData = Object.entries(tradeData).filter(([key]) => {
      const [year, month] = key.split('-').map(Number);
      return year === selectedYear && month === selectedMonth + 1;
    }).map(([, data]) => data);

    const totalPnL = monthData.reduce((sum, d) => sum + d.net, 0);
    const totalTrades = monthData.reduce((sum, d) => sum + (d.trades || 1), 0);
    const wins = monthData.filter(d => d.net > 0);
    const losses = monthData.filter(d => d.net < 0);
    const highestWin = wins.length > 0 ? Math.max(...wins.map(d => d.net)) : 0;
    const highestLoss = losses.length > 0 ? Math.min(...losses.map(d => d.net)) : 0;

    return {
      monthlyPnL: totalPnL,
      totalTrades,
      highestWin,
      highestLoss,
      overallPnL: Object.values(tradeData).reduce((sum, d) => sum + d.net, 0),
    };
  }, [selectedMonth, selectedYear, tradeData]);

  const miniCalendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth);
    const weeks = [];
    let currentWeek = [];

    for (let i = 0; i < firstDay; i++) {
      currentWeek.push('');
    }

    for (let day = 1; day <= daysInMonth; day++) {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push('');
      }
      weeks.push(currentWeek);
    }

    return weeks;
  }, [selectedMonth, selectedYear]);

  const monthTrades = useMemo(() => {
    return Object.entries(tradeData)
      .filter(([key]) => {
        const [year, month] = key.split('-').map(Number);
        return year === selectedYear && month === selectedMonth + 1;
      })
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => b.net - a.net);
  }, [selectedMonth, selectedYear, tradeData]);

  return (
    <div className="traders-diary-page">
      <header className="traders-header">
        <div className="header-left s4u-page-heading">
          <button className="back-btn s4u-back-btn" onClick={onBack}>← Back to Dashboard</button>
          <h1>Traders Diary</h1>
        </div>
        <div className="year-selector">
          <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027, 2028].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </header>

      <div className="month-tabs">
        {months.map((month, idx) => (
          <button
            className={`month-tab ${idx === selectedMonth ? 'active' : ''}`}
            key={month}
            onClick={() => setSelectedMonth(idx)}
          >
            {month}
          </button>
        ))}
      </div>

      <div className="calendar-section">
        <div className="calendar-grid">
          {calendarDays.map((dayInfo, idx) => (
            <div
              key={idx}
              className={`day-box ${dayInfo.type === 'day' ? (dayInfo.hasProfit ? 'green' : dayInfo.hasLoss ? 'red' : '') : 'empty'}`}
              onClick={() => dayInfo.type === 'day' && handleDayClick(dayInfo.day)}
            >
              {dayInfo.type === 'day' ? (
                <>
                  <div className="day-number">{String(dayInfo.day).padStart(2, '0')}</div>
                  {dayInfo.data && (
                    <>
                      <div className="net">{dayInfo.data.net > 0 ? '+' : ''}{dayInfo.data.net} (Net)</div>
                      <div className="label">{dayInfo.data.label}</div>
                      <div className="plus">{dayInfo.data.net > 0 ? '+' : '-'}</div>
                    </>
                  )}
                </>
              ) : null}
            </div>
          ))}
        </div>
        <div className="calendar-mini">
          <h3>{months[selectedMonth]} {selectedYear}</h3>
          <table>
            <thead>
              <tr>{['M','T','W','T','F','S','S'].map((day, idx) => <th key={idx}>{day}</th>)}</tr>
            </thead>
            <tbody>
              {miniCalendarDays.map((week, rowIdx) => (
                <tr key={rowIdx}>
                  {week.map((day, colIdx) => {
                    const dateKey = day ? `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
                    const dayData = dateKey ? tradeData[dateKey] : null;
                    const dayClass = dayData ? (dayData.net > 0 ? 'mini-profit' : dayData.net < 0 ? 'mini-loss' : '') : '';
                    return (
                      <td key={colIdx} className={`${day ? 'has-day' : ''} ${dayClass}`}>{day}</td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="statistics-section">
        <h2>Statistics</h2>
        <div className="stats-grid">
          <div className="stat-box">
            <div className="stat-title">Monthly P&L</div>
            <div className={`stat-value ${stats.monthlyPnL >= 0 ? 'positive' : 'negative'}`}>
              {stats.monthlyPnL >= 0 ? '+' : ''}{stats.monthlyPnL}
            </div>
          </div>
          <div className="stat-box">
            <div className="stat-title">Total Trades</div>
            <div className="stat-value">{stats.totalTrades}</div>
          </div>
          <div className="stat-box">
            <div className="stat-title">Highest Wins</div>
            <div className="stat-value positive">+{stats.highestWin}</div>
          </div>
          <div className="stat-box">
            <div className="stat-title">Highest Loss</div>
            <div className="stat-value negative">{stats.highestLoss}</div>
          </div>
          <div className="stat-box">
            <div className="stat-title">Overall P&L</div>
            <div className={`stat-value ${stats.overallPnL >= 0 ? 'positive' : 'negative'}`}>
              {stats.overallPnL >= 0 ? '+' : ''}{stats.overallPnL}
            </div>
          </div>
          <div className="stat-box">
            <div className="stat-title">Win Rate</div>
            <div className="stat-value">
              {stats.totalTrades > 0 ? Math.round((monthTrades.filter(t => t.net > 0).length / stats.totalTrades) * 100) : 0}%
            </div>
          </div>
        </div>
      </div>

      <div className="trades-section">
        <h2>Trades</h2>
        <div className="trades-box">
          <div className="trade-header">
            <button className="active">Top Winning Trades</button>
            <button>Top Losing Trades</button>
          </div>
          <div className="trade-list">
            {monthTrades.length === 0 ? (
              <div className="trade-item">No trades recorded for this month</div>
            ) : (
              monthTrades.map((trade) => (
                <div className="trade-item" key={trade.date}>
                  <span> {trade.label} <small>({trade.date})</small></span>
                  <span className={`change ${trade.net > 0 ? 'positive' : 'negative'}`}>
                    {trade.net > 0 ? '+' : ''}{trade.net}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modal for adding/editing trade */}
      {showModal && (
        <div className="trade-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="trade-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add Trade - {months[selectedMonth]} {selectedDay}, {selectedYear}</h3>
            
            <div className="trade-type-selector">
              <button
                className={`profit-btn ${formData.type === 'profit' ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, type: 'profit' })}
              >
                + Profit
              </button>
              <button
                className={`loss-btn ${formData.type === 'loss' ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, type: 'loss' })}
              >
                - Loss
              </button>
            </div>

            <div className="form-group">
              <label>Amount</label>
              <input
                type="number"
                value={formData.net}
                onChange={(e) => setFormData({ ...formData, net: e.target.value })}
                placeholder="Enter amount"
              />
            </div>

            <div className="form-group">
              <label>Label/Note</label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="e.g., Tech Rally, Stop Loss"
              />
            </div>

            <div className="modal-actions">
              <button className="save-btn" onClick={handleSaveTrade}>Save</button>
              {tradeData[`${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`] && (
                <button className="delete-btn" onClick={handleDeleteTrade}>Delete</button>
              )}
              <button className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradersDiary;